import moment from 'moment';
import { rgba } from 'polished';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useHistory, useParams } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';
import {
	CycleManager,
	CycleProgress,
	useCycleAnalytics,
	useCycleTemplatesList,
} from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import { HistoryIcon, SendIcon } from '@treams/treams-icons';
import { CycleTemplateInterval } from '@treams/treams-network';
import {
	Container,
	Delta,
	Empty,
	FlexGap,
	Heading3,
	Jumbo,
	Paragraph,
	Select,
	Separator,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { useGroupSelection } from '../../goals/group-selector/new/useGroupSelection';
import { AnalyticsFilter, canSelectDepartment } from '../components/AnalyticsFilter';
import {
	CycleAnalyticsSkeleton,
	CycleExcelExportButton,
	CycleManagersGrid,
	CycleMomentsGrid,
	CycleMomentsTimeline,
	CycleProgressChart,
	LegendSwatch,
	type Manager,
	ManagerStatusModal,
	MomentIcon,
	RemindManagersModal,
} from './components';
import { CycleDetails, DetailsList } from './components/CycleDetails';
import { ProgressLabel } from './components/GridProgressCell';

const AnalyticsCard = styled(Box)`
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.md}px;
	background-color: ${tokens.surface.white};
	min-width: 0;
`;

const GridClip = styled.div`
	min-width: 0;
	width: 100%;
	max-width: 100%;
	position: relative;
	z-index: 0;
	contain: layout;

	&& .ag-layout-auto-height.ag-root,
	&& .ag-layout-auto-height.ag-root-wrapper {
		overflow: hidden;
	}
`;

const StickyRemindFooter = styled(Box)`
	position: sticky;
	bottom: 0;
	z-index: 2;
	background-color: ${tokens.surface.white};
	// This footer carries the card's bottom border so there is only ever one
	// 1px line. The card itself drops its bottom border (see ManagersCard);
	// otherwise the pinned separator and the card's own scrolling border briefly
	// line up and read as a single 2px line mid-scroll.
	border-bottom: 1px solid ${tokens.border.subtle};
	border-bottom-left-radius: ${tokens.radius.md}px;
	border-bottom-right-radius: ${tokens.radius.md}px;
`;

function ContinuousCycleProgressLegend({ progress }: { progress: CycleProgress }) {
	const theme = useTheme();
	const { t } = useTranslation();

	return (
		<DetailsList
			items={[
				{
					icon: <LegendSwatch swatchColor={theme.colors.primaryLight} />,
					title: t('analytics.cycles.progress.total'),
					description: (
						<ProgressLabel value={progress.total - progress.started} total={progress.total} />
					),
				},
				{
					icon: <LegendSwatch swatchColor={rgba(theme.colors.primary, 0.5)} />,
					title: t('analytics.cycles.progress.started'),
					description: (
						<ProgressLabel value={progress.started - progress.completed} total={progress.total} />
					),
				},
				{
					icon: <LegendSwatch swatchColor={theme.colors.primary} />,
					title: t('analytics.cycles.progress.completed'),
					description: <ProgressLabel value={progress.completed} total={progress.total} />,
				},
			]}
		/>
	);
}

export function ratio(value: number, total: number) {
	return total > 0 ? value / total : 0;
}

function toManager(manager: CycleManager): Manager {
	return {
		id: manager.user.id,
		name: `${manager.user.firstName} ${manager.user.lastName}`.trim(),
	};
}

// A manager is considered "done" for a moment when every progress step has
// reached its total (100%). The relevant steps differ per moment type, so we
// just check all values rather than a fixed set of keys.
function isManagerMomentComplete(manager: CycleManager, momentID: string): boolean {
	const moment = manager.moments.find((moment) => moment.id === momentID);

	if (!moment) {
		return true;
	}

	const { total, ...steps } = moment.progress;

	// Nothing to complete (no managed employees in this moment), so there is no
	// point reminding this manager.
	if (total === 0) {
		return true;
	}

	return Object.values(steps).every((value) => value === undefined || value >= total);
}

export function CycleAnalytics(props: { headerFooter?: ReactNode }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const { cycleTemplateID } = useParams<{ cycleTemplateID?: string }>();
	const { lastSyncedAt, status } = useAnalytics();

	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear);
	const [roleID, setRoleID] = useState<string | undefined>(undefined);
	const [momentID, setMomentID] = useState<string | undefined>(undefined);
	const [remindManagers, setRemindManagers] = useState<Manager[] | null>(null);
	const [selectedManagers, setSelectedManagers] = useState<Manager[]>([]);
	const [selectionMomentID, setSelectionMomentID] = useState(momentID);

	// Reset the selection synchronously when the moment changes, before the rest
	// of the render reads selectedManagers. Doing this in an effect would leave a
	// render where momentID is new but the selection still holds the previous
	// moment's managers, so the sticky footer (and its send action) could act on
	// stale rows.
	if (momentID !== selectionMomentID) {
		setSelectionMomentID(momentID);
		setSelectedManagers([]);
	}

	const groupSelection = useGroupSelection({
		setPermissionBasedDefault: true,
		canSelect: canSelectDepartment,
	});

	// `useGroupSelection` fetches groups filtered to `isArchived: false`, giving
	// us the set of active group ids to filter archived departments from the table.
	const activeGroupIDs = useMemo(
		() => new Set(groupSelection.allGroups.map((group) => group.id)),
		[groupSelection.allGroups],
	);

	const startDate = useMemo(() => new Date(year, 0, 1), [year]);
	const endDate = useMemo(() => new Date(year, 11, 31, 23, 59, 59, 999), [year]);

	// Stabilise by value: groupsInSelection gets a new array identity every render,
	// so key on the sorted ids to avoid re-triggering the data fetch each render.
	const groupKey = groupSelection.groupsInSelection
		.map((group) => group.id)
		.sort()
		.join(',');
	const groupIDs = useMemo(() => (groupKey ? groupKey.split(',') : []), [groupKey]);

	const { cycleAnalytics, loading, findPendingEmployees } = useCycleAnalytics(
		cycleTemplateID,
		startDate,
		endDate,
		groupIDs,
		roleID,
	);

	const availableYears = useMemo(() => cycleAnalytics?.availableYears ?? [], [cycleAnalytics]);

	// Managers who haven't reached 100% on every step of their row for a given
	// moment; these are the ones worth reminding. Used both by the footer (for
	// the selected moment) and by each moment's timeline tooltip.
	const getManagersToRemind = useCallback(
		(targetMomentID: string): Manager[] =>
			(cycleAnalytics?.manager ?? [])
				.filter((manager) => !isManagerMomentComplete(manager, targetMomentID))
				.map(toManager),
		[cycleAnalytics],
	);

	// The selected rows re-filtered against the managers that still need a
	// reminder: a selected row may have completed since selection (e.g. after an
	// analytics refresh). Drives both the send action's disabled state and the
	// list handed to the remind modal.
	const remindableSelectedManagers = useMemo(() => {
		if (!momentID) return [];

		const remindableIDs = new Set(getManagersToRemind(momentID).map((manager) => manager.id));

		return selectedManagers.filter((manager) => remindableIDs.has(manager.id));
	}, [momentID, selectedManagers, getManagersToRemind]);

	const { templates, defaultCycleTemplateID, loading: templatesLoading } = useCycleTemplatesList();

	const hasNoCycleTemplates = !templatesLoading && templates.length === 0;

	// On first load, default to the cycle the workspace is currently running:
	// the template of the most recently activated cycle instance, falling back
	// to the first template when the snapshot holds no activated instance.
	useEffect(() => {
		if (templatesLoading || cycleTemplateID) return;

		const defaultID = defaultCycleTemplateID ?? templates[0]?.id;

		if (!defaultID) return;

		history.replace(`/analytics/cycles/${defaultID}`);
	}, [cycleTemplateID, templates, templatesLoading, defaultCycleTemplateID, history]);

	useEffect(() => {
		if (!cycleAnalytics || cycleAnalytics.moments.length === 0) return;

		setMomentID((current) =>
			current && cycleAnalytics.moments.some((moment) => moment.id === current)
				? current
				: cycleAnalytics.moments[0].id,
		);
	}, [cycleAnalytics]);

	return (
		<>
			<Jumbo
				title={t('analytics.overview.title')}
				footer={props.headerFooter}
				button={
					<Flex ml="auto" alignItems="center" justifyContent="flex-end" flexWrap="wrap">
						{lastSyncedAt ? (
							<Paragraph mr={2} small>
								{t('analytics.overview.lastSyncedLabel', {
									time: moment(lastSyncedAt).format('LL'),
								})}
							</Paragraph>
						) : (
							<Paragraph mr={2} small>
								{t('analytics.snapshots.checking')}
							</Paragraph>
						)}
					</Flex>
				}
				wide={true}
			/>
			<>
				{hasNoCycleTemplates && (
					<Container wide={true}>
						<Flex py={5} alignItems="center" justifyContent="center">
							<Empty
								icon={<HistoryIcon />}
								title={t('analytics.cycles.empty.title')}
								description={t('analytics.cycles.empty.description')}
							/>
						</Flex>
					</Container>
				)}
				{!hasNoCycleTemplates && (
					<AnalyticsFilter
						cycleTemplateID={cycleTemplateID}
						cycleTemplateOptions={templates.map((template) => ({
							title: localize(template.title).text ?? '',
							value: template.id,
						}))}
						onCycleTemplateChange={(newCycleTemplateID) =>
							history.push(`/analytics/cycles/${newCycleTemplateID}`)
						}
						year={year}
						years={availableYears}
						onYearChange={setYear}
						roleID={roleID}
						onRoleChange={setRoleID}
						departments={groupSelection}
						onDepartmentChange={groupSelection.updateExplicitGroupSelection}
					/>
				)}
				{!hasNoCycleTemplates &&
					(loading || (status === AnalyticsStatus.Succeeded && !cycleAnalytics)) && (
						<CycleAnalyticsSkeleton />
					)}
				{!hasNoCycleTemplates && !loading && status !== AnalyticsStatus.Succeeded && (
					<Box style={{ position: 'relative', maxHeight: '500px', overflow: 'hidden' }}>
						<Skeleton.Empty contents={null}>
							<CycleAnalyticsSkeleton />
						</Skeleton.Empty>
						<Box
							style={{
								position: 'absolute',
								bottom: 0,
								left: 0,
								width: '100%',
								height: '400px',
								background: `linear-gradient(to bottom, transparent, white)`,
								pointerEvents: 'none',
							}}
						/>
						<Box
							style={{
								zIndex: 4,
								position: 'absolute',
								left: '50%',
								top: '50%',
								transform: 'translate(-50%, -70%)',
							}}
						>
							<Empty
								icon={<HistoryIcon />}
								title={t('analytics.cycles.notReady.title')}
								description={t('analytics.cycles.notReady.description')}
							/>
						</Box>
					</Box>
				)}
				{!hasNoCycleTemplates &&
					!loading &&
					status === AnalyticsStatus.Succeeded &&
					cycleAnalytics && (
						<Container py={4} wide={true} minWidth={0}>
							<FlexGap mb={3} gap={tokens.gap.list} alignItems="stretch">
								<AnalyticsCard flex={1}>
									<Box p={3}>
										<Heading3 mb={2}>{t('analytics.cycles.progress.title')}</Heading3>
										<Paragraph>{t('analytics.cycles.progress.description')}</Paragraph>
										<Box mt={3}>
											<FlexGap alignItems="center" gap={tokens.gap.group}>
												<Flex flex={5} minWidth={0} flexDirection="column" alignItems="center">
													<CycleProgressChart
														completed={cycleAnalytics.momentProgress.completed}
														started={
															cycleAnalytics.cycleTemplate.interval !== CycleTemplateInterval.Year
																? cycleAnalytics.momentProgress.started
																: undefined
														}
														total={cycleAnalytics.momentProgress.total}
													/>
													<Paragraph mt={2}>
														{cycleAnalytics.momentProgress.completed} /{' '}
														{cycleAnalytics.momentProgress.total}{' '}
														{t('analytics.cycles.progress.completedMoments')}
													</Paragraph>
												</Flex>
												<Box flex={7} minWidth={0}>
													{cycleAnalytics.cycleTemplate.interval !== CycleTemplateInterval.Year && (
														<ContinuousCycleProgressLegend
															progress={cycleAnalytics.momentProgress}
														/>
													)}
													<Flex
														mt={
															cycleAnalytics.cycleTemplate.interval !== CycleTemplateInterval.Year
																? 3
																: 0
														}
													>
														<Delta
															currentValue={ratio(
																cycleAnalytics.momentProgress.completed,
																cycleAnalytics.momentProgress.total,
															)}
															previousValue={ratio(
																cycleAnalytics.momentProgress.previousCompleted,
																cycleAnalytics.momentProgress.previousTotal,
															)}
															label={t('analytics.cycles.progress.delta')}
														/>
													</Flex>
												</Box>
											</FlexGap>
										</Box>
									</Box>
								</AnalyticsCard>

								{cycleAnalytics.cycleTemplate && (
									<AnalyticsCard flex={1}>
										<Box p={3}>
											<Heading3 mb={2}>
												{t('analytics.cycles.details.title', {
													cycleTemplate: localize(cycleAnalytics.cycleTemplate.title)?.text ?? '',
												})}
											</Heading3>
											<Paragraph mb={3}>{t('analytics.cycles.details.description')}</Paragraph>
											<CycleDetails
												cycleTemplateID={cycleAnalytics.cycleTemplateID}
												cycleTemplate={cycleAnalytics.cycleTemplate}
												employeeCount={cycleAnalytics.employeeCount}
												startDate={cycleAnalytics.startDate}
												endDate={cycleAnalytics.endDate}
												onClickCycleTemplate={(cycleTemplateID) =>
													history.push(`/analytics/cycles/${cycleTemplateID}`)
												}
											/>
										</Box>
									</AnalyticsCard>
								)}
							</FlexGap>

							<AnalyticsCard mb={3}>
								<Box p={3}>
									<Heading3 mb={2}>{t('analytics.cycles.moments.title')}</Heading3>
									<Paragraph>{t('analytics.cycles.moments.description')}</Paragraph>

									{cycleAnalytics.startDate &&
										cycleAnalytics.endDate &&
										cycleAnalytics.cycleTemplate.interval === CycleTemplateInterval.Year && (
											<Box mt={3} minWidth={0} overflow="hidden">
												<CycleMomentsTimeline
													moments={cycleAnalytics.moments.flatMap((moment) => {
														if (!moment.startDate || !moment.endDate) {
															return [];
														}

														return [
															{
																...moment,
																startDate: moment.startDate,
																endDate: moment.endDate,
															},
														];
													})}
													startDate={cycleAnalytics.startDate}
													endDate={cycleAnalytics.endDate}
													getManagersToRemind={getManagersToRemind}
													onRemind={setRemindManagers}
												/>
											</Box>
										)}
								</Box>

								<Box
									mt={cycleAnalytics.cycleTemplate.interval === CycleTemplateInterval.Year ? 3 : 0}
									minWidth={0}
								>
									<GridClip>
										<CycleMomentsGrid
											moments={cycleAnalytics.moments}
											interval={cycleAnalytics.cycleTemplate.interval}
										/>
									</GridClip>
								</Box>
							</AnalyticsCard>

							<AnalyticsCard>
								<Box p={3}>
									<Heading3 mb={2}>{t('analytics.cycles.managers.title')}</Heading3>
									<Paragraph>{t('analytics.cycles.managers.description')}</Paragraph>

									<FlexGap mt={3} alignItems="center" gap={tokens.gap.list}>
										{momentID && (
											<Box flex={1} minWidth={0}>
												<Select
													selectedValue={momentID}
													options={cycleAnalytics.moments.map((moment) => ({
														title: localize(moment.title).text ?? '',
														value: moment.id,
														icon: <MomentIcon momentType={moment.type} size={16} />,
													}))}
													onChange={(newMomentID) => {
														if (newMomentID) {
															setMomentID(newMomentID);
														}
													}}
												/>
											</Box>
										)}
										<CycleExcelExportButton
											cycleAnalytics={cycleAnalytics}
											activeGroupIDs={activeGroupIDs}
											year={year}
										/>
									</FlexGap>
								</Box>

								{momentID && (
									<GridClip>
										<CycleManagersGrid
											managers={cycleAnalytics.manager}
											activeGroupIDs={activeGroupIDs}
											momentID={momentID}
											momentType={
												cycleAnalytics.moments.find((moment) => moment.id === momentID)?.type
											}
											onSelectedManagersChange={setSelectedManagers}
											onSelectManager={(managerID) => {
												if (!momentID || !cycleTemplateID) return;

												history.push({
													pathname: `/analytics/cycles/${cycleTemplateID}/moment/${momentID}/managers/${managerID}`,
													state: { preventScrollReset: true },
												});
											}}
										/>
									</GridClip>
								)}

								{selectedManagers.length > 0 && (
									<StickyRemindFooter pb={3}>
										<Separator />

										<Box mt={3} mx={3}>
											<Select
												fit
												placeholder={t('common.actions')}
												options={[
													{
														title: t('analytics.cycles.managers.remind.send'),
														value: 'remind',
														icon: <SendIcon />,
														disabled: remindableSelectedManagers.length === 0,
													},
												]}
												onChange={(value) => {
													if (value === 'remind') {
														setRemindManagers(remindableSelectedManagers);
													}
												}}
											/>
										</Box>
									</StickyRemindFooter>
								)}
							</AnalyticsCard>
						</Container>
					)}
			</>
			{momentID &&
				cycleTemplateID &&
				remindManagers &&
				remindManagers.length > 0 &&
				cycleAnalytics && (
					<RemindManagersModal
						close={() => setRemindManagers(null)}
						cycleTemplateID={cycleTemplateID}
						momentID={momentID}
						findPendingEmployees={findPendingEmployees}
						momentName={
							localize(cycleAnalytics.moments.find((moment) => moment.id === momentID)?.title)
								.text ?? ''
						}
						managers={remindManagers}
					/>
				)}
			<Route path={`/analytics/cycles/:cycleTemplateID/moment/:momentID/managers/:managerID`}>
				{({ match }) => {
					if (!match || !cycleAnalytics || !cycleTemplateID) {
						return null;
					}

					const { momentID: routeMomentID, managerID } = match.params;
					const manager = cycleAnalytics.manager.find((item) => item.user.id === managerID);
					const managerMoment = manager?.moments.find((item) => item.id === routeMomentID);

					if (!manager || !managerMoment) {
						return null;
					}

					return (
						<ManagerStatusModal
							close={() =>
								history.push({
									pathname: `/analytics/cycles/${cycleTemplateID}`,
									state: { preventScrollReset: true },
								})
							}
							managerName={`${manager.user.firstName} ${manager.user.lastName}`.trim()}
							moment={cycleAnalytics.moments.find((item) => item.id === routeMomentID)}
							participants={managerMoment.participants}
							statuses={managerMoment.statuses}
						/>
					);
				}}
			</Route>
		</>
	);
}
